import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: any | null;
  roles: string[];
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: any }>;
  signUp: (email: string, password: string, tenantName: string, fullName: string) => Promise<{ error?: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Fetch user profile with timeout
          setTimeout(async () => {
            if (!mounted) return;
            
            try {
              const { data: profileData, error } = await supabase
                .from('profiles')
                .select(`
                  *,
                  tenants:tenant_id (
                    id,
                    name,
                    slug
                  )
                `)
                .eq('user_id', session.user.id)
                .maybeSingle();
              
              if (!mounted) return;
              
              if (error) {
                console.error('Profile fetch error:', error);
                setProfile(null);
              } else if (profileData) {
                setProfile(profileData);
              } else {
                // No profile exists yet – create a default tenant and profile
                try {
                  const meta = (session.user as any).user_metadata || {};
                  const fullName = meta.full_name || session.user.email?.split('@')[0] || 'New User';
                  const tenantName = meta.tenant_name || `${fullName}'s Workspace`;
                  
                  // Generate unique slug with fallback mechanism
                  const generateUniqueSlug = async (baseName: string): Promise<string> => {
                    const baseSlug = baseName.toLowerCase()
                      .replace(/[^a-z0-9\s-]/g, '')
                      .replace(/\s+/g, '-')
                      .replace(/-+/g, '-')
                      .trim();

                    // If slug ended up empty, seed with generic prefix
                    if (!baseSlug) {
                      return `workspace-${Math.random().toString(36).substring(2, 8)}`;
                    }

                    // Try base slug first
                    const { data: existing } = await supabase
                      .from('tenants')
                      .select('slug')
                      .eq('slug', baseSlug)
                      .maybeSingle();

                    if (!existing) {
                      return baseSlug;
                    }

                    // If base slug exists, add random suffix
                    const randomSuffix = Math.random().toString(36).substring(2, 8);
                    return `${baseSlug}-${randomSuffix}`;
                  };

                  // Start with a candidate slug
                  const baseCandidate = await generateUniqueSlug(tenantName);

                  // Race-safe insert with retries on unique constraint
                  let tenantRecord: any = null;
                  let attemptSlug = baseCandidate;
                  for (let i = 0; i < 5; i++) {
                    const { data, error } = await supabase
                      .from('tenants')
                      .insert([{ name: tenantName, slug: attemptSlug, created_by: session.user.id }])
                      .select()
                      .single();

                    if (!error && data) {
                      tenantRecord = data;
                      break;
                    }

                    const duplicate = error?.code === '23505' || error?.message?.includes('tenants_slug_key');
                    if (duplicate) {
                      // Generate a new unique suffix and retry
                      attemptSlug = `${baseCandidate}-${Math.random().toString(36).substring(2, 8)}`;
                      continue;
                    }

                    // Other errors: stop early
                    console.error('Tenant creation error:', error);
                    throw error;
                  }

                  // Final fallback: if still no record, try to fetch by last attempted slug
                  if (!tenantRecord) {
                    const { data: fetched } = await supabase
                      .from('tenants')
                      .select('*')
                      .eq('slug', attemptSlug)
                      .maybeSingle();
                    if (fetched) {
                      tenantRecord = fetched;
                    } else {
                      throw new Error('Kon geen unieke tenant aanmaken. Probeer het later opnieuw.');
                    }
                  }

                  if (tenantRecord?.id) {
                    const { data: createdProfile, error: profileCreateError } = await supabase
                      .from('profiles')
                      .insert([
                        {
                          user_id: session.user.id,
                          tenant_id: tenantRecord.id,
                          organization_id: tenantRecord.id, // Keep both for consistency
                          full_name: fullName,
                          role: 'admin'
                        }
                      ])
                      .select(`
                        *,
                        tenants:tenant_id (
                          id,
                          name,
                          slug
                        )
                      `)
                      .single();

                    if (profileCreateError) {
                      console.error('Profile create error:', profileCreateError);
                      setProfile(null);
                    } else {
                      setProfile(createdProfile);
                      
                      // Create audit log for successful profile creation
                      try {
                        await supabase.functions.invoke('audit-log', {
                          body: {
                            user_id: session.user.id,
                            action_type: 'signup',
                            resource_type: 'auth',
                            success: true,
                            risk_level: 'low'
                          }
                        });
                      } catch (auditError) {
                        console.warn('Failed to create audit log:', auditError);
                      }
                    }
                  } else {
                    console.error('Failed to create or find tenant');
                    setProfile(null);
                  }
                } catch (createErr) {
                  console.error('Auto-create profile error:', createErr);
                  setProfile(null);
                }
              }
            } catch (error) {
              if (!mounted) return;
              console.error('Profile fetch error:', error);
              setProfile(null);
            } finally {
              if (mounted) {
                setLoading(false);
              }
            }
          }, 0);
        } else {
          setProfile(null);
          setLoading(false);
        }
      }
    );

    // Check for existing session
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!mounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (!session) {
          setLoading(false);
        }
      } catch (error) {
        if (mounted) {
          console.error('Session check error:', error);
          setLoading(false);
        }
      }
    };

    checkSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    // Create audit log for login attempt - do this after profile is loaded
    if (data.user && !error) {
      // We'll create the audit log after profile is fetched in the auth state change handler
      console.log('Login successful for user:', data.user.id);
    }
    
    return { error };
  };

  const signUp = async (email: string, password: string, tenantName: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          tenant_name: tenantName,
          full_name: fullName,
        }
      }
    });

    if (error) return { error };

    // Create tenant and profile
    if (data.user) {
      const tenantSlug = tenantName.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();

      const { data: tenant, error: tenantError } = await supabase
        .from('tenants')
        .insert([
          {
            name: tenantName,
            slug: tenantSlug,
          }
        ])
        .select()
        .single();

      if (tenantError) return { error: tenantError };

      const { error: profileError } = await supabase
        .from('profiles')
        .insert([
          {
            user_id: data.user.id,
            tenant_id: tenant.id,
            full_name: fullName,
            role: 'admin'
          }
        ]);

      if (profileError) return { error: profileError };
    }

    return { error };
  };

  const signOut = async () => {
    // Clear local state immediately
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
    await supabase.auth.signOut();
  };

  const value = {
    user,
    session,
    profile,
    roles,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}